import { useState, useEffect, useCallback } from "react";
import axios from "../lib/api";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

const ROLE_BADGES = {
  super_admin: "bg-amber-100 text-amber-700",
  support_admin: "bg-blue-100 text-blue-700",
  user: "bg-slate-100 text-slate-600",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set("search", search);
      if (roleFilter) params.set("role", roleFilter);
      const res = await axios.get(`${API}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Usuarios</h1>
        <span className="text-sm text-slate-500">{total} total</span>
      </div>

      <div className="flex flex-wrap gap-3">
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); fetchUsers(); }} className="flex gap-2 flex-1 min-w-[200px] max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input className="pl-9" placeholder="Buscar por email o nombre..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-users-search" />
          </div>
          <Button type="submit" variant="outline" size="sm">Buscar</Button>
        </form>
        <select
          className="border border-slate-200 rounded-md px-3 py-1.5 text-sm"
          value={roleFilter}
          onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
          data-testid="admin-users-role-filter"
        >
          <option value="">Todos los roles</option>
          <option value="user">User</option>
          <option value="support_admin">Support Admin</option>
          <option value="super_admin">Super Admin</option>
        </select>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Email</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Nombre</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Rol</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Organización</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Plan</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Creado</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-400">Cargando...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-400">No se encontraron usuarios</td></tr>
                ) : users.map((u) => (
                  <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-slate-900">{u.email}</td>
                    <td className="px-4 py-3 text-slate-600">{u.name || "-"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_BADGES[u.role] || ROLE_BADGES.user}`}>
                        {u.role || "user"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{u.organization?.name || "-"}</td>
                    <td className="px-4 py-3 text-slate-600 capitalize">{u.organization?.plan_key || "-"}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString("es-ES") : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Página {page} de {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
